import { useState, useRef, useCallback, useEffect } from 'react';
import { eventService } from '../services';

const DEBOUNCE_MS = 1500;
const STATUS_RESET_MS = 3000;

/**
 * useAutoSave - Auto-saves event form data on blur/debounce
 *
 * Create mode: auto-creates a draft when title is typed, returns new eventId
 * Edit mode: calls updateEvent with only changed fields
 */
export default function useAutoSave({ eventId, formData, houseRules, partySupplies, isPriceLocked }) {
  const [saveStatus, setSaveStatus] = useState('idle');
  const [autoSaveEventId, setAutoSaveEventId] = useState(eventId || null);

  const lastSavedRef = useRef(null);
  const debounceRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const isCreatingRef = useRef(false);

  // Keep autoSaveEventId in sync if eventId prop changes (e.g. navigated to edit mode)
  useEffect(() => {
    if (eventId) setAutoSaveEventId(eventId);
  }, [eventId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    };
  }, []);

  const dollarsToCents = (dollars) => {
    const val = parseFloat(dollars);
    return isNaN(val) ? 0 : Math.round(val * 100);
  };

  const buildEventData = useCallback(() => {
    const filteredRules = houseRules.filter((r) => r.trim());
    const filteredSupplies = partySupplies.filter((s) => s.text.trim());

    const data = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      locationAddress: formData.location.trim(),
      mode: formData.mode,
      houseRules: filteredRules,
      partySupplies: filteredSupplies.map((s) => s.text.trim()),
    };

    if (formData.fundingGoal) {
      data.fundingGoalCents = dollarsToCents(formData.fundingGoal);
    }
    if (formData.ticketPrice && !isPriceLocked) {
      data.ticketPriceCents = dollarsToCents(formData.ticketPrice);
    }
    if (formData.dateTime) {
      data.dateTime = new Date(formData.dateTime).toISOString();
    }

    return data;
  }, [formData, houseRules, partySupplies, isPriceLocked]);

  const getCurrentSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      houseRules,
      partySupplies: partySupplies.map((s) => s.text),
    });
  }, [formData, houseRules, partySupplies]);

  const hasChanges = useCallback(() => {
    if (!lastSavedRef.current) return true;
    return getCurrentSnapshot() !== lastSavedRef.current;
  }, [getCurrentSnapshot]);

  const setStatusWithReset = useCallback((status) => {
    setSaveStatus(status);
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    if (status === 'saved' || status === 'error') {
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), STATUS_RESET_MS);
    }
  }, []);

  const performSave = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!hasChanges()) return;

    const currentId = autoSaveEventId;

    // Create mode: need title to create a draft
    if (!currentId) {
      if (!formData.title.trim() || isCreatingRef.current) return;

      isCreatingRef.current = true;
      isSavingRef.current = true;
      setStatusWithReset('saving');

      try {
        const eventData = {
          title: formData.title.trim(),
          description: formData.description.trim() || 'Draft event',
          dateTime: formData.dateTime
            ? new Date(formData.dateTime).toISOString()
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          locationAddress: formData.location.trim() || 'TBD',
          mode: formData.mode,
          fundingGoalCents: dollarsToCents(formData.fundingGoal) || 0,
          ticketPriceCents: dollarsToCents(formData.ticketPrice) || 0,
          houseRules: houseRules.filter((r) => r.trim()),
          partySupplies: partySupplies.filter((s) => s.text.trim()).map((s) => s.text.trim()),
        };

        const result = await eventService.createEvent(eventData);
        if (result.success) {
          const newId = result.data.id;
          setAutoSaveEventId(newId);
          lastSavedRef.current = getCurrentSnapshot();
          setStatusWithReset('saved');
          return newId;
        } else {
          setStatusWithReset('error');
        }
      } catch {
        setStatusWithReset('error');
      } finally {
        isSavingRef.current = false;
        isCreatingRef.current = false;
      }
      return null;
    }

    // Edit mode: update existing event
    isSavingRef.current = true;
    setStatusWithReset('saving');

    try {
      const eventData = buildEventData();
      const result = await eventService.updateEvent(currentId, eventData);
      if (result.success) {
        lastSavedRef.current = getCurrentSnapshot();
        setStatusWithReset('saved');
      } else {
        setStatusWithReset('error');
      }
    } catch {
      setStatusWithReset('error');
    } finally {
      isSavingRef.current = false;
    }
  }, [autoSaveEventId, formData, houseRules, partySupplies, hasChanges, getCurrentSnapshot, buildEventData, setStatusWithReset]);

  const triggerSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    performSave();
  }, [performSave]);

  const debouncedSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSave();
    }, DEBOUNCE_MS);
  }, [performSave]);

  // Initialize lastSavedRef when first loading in edit mode
  useEffect(() => {
    if (eventId && !lastSavedRef.current) {
      lastSavedRef.current = getCurrentSnapshot();
    }
  }, [eventId, getCurrentSnapshot]);

  return {
    saveStatus,
    triggerSave,
    debouncedSave,
    autoSaveEventId,
  };
}
